import { Link } from 'react-router-dom';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import swal from "sweetalert";
import { useDispatch, useSelector } from 'react-redux';
import { useState, useCallback } from 'react';
import { FormInput } from '../../../components/Form';
import Hero from '../../../pages/Help/Hero';
import FAQs from '../../../pages/Help/FAQs';
import { addHelpThunk } from '../../../store/help/helpThunk';
import { resetHelpState } from '../../../store/help/helpSlice';
import ReCAPTCHA from "react-google-recaptcha";
import axios from 'axios';
import { toast } from 'react-toastify';

const HelpAndSupport = () => {
    const dispatch = useDispatch();
    const { addHelpThunkLoading, addHelpThunkError, addHelpThunkFullfill } = useSelector((state) => state.help);
    const [dragActive, setDragActive] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [fileError, setFileError] = useState('');

    const schemaResolver = yupResolver(
        yup.object().shape({
            fullName: yup.string().required("Full name is required"),
            mobile: yup
                .string()
                .required("Mobile number is required")
                .matches(/^\d{10}$/, "Mobile number must be 10 digits"),
            subject: yup.string().required("Subject is required"),
            description: yup.string().required("Description is required"),
            attachments: yup
                .mixed()
                .required("Image file is required")
        })
    );

    const methods = useForm({ resolver: schemaResolver });
    const {
        handleSubmit,
        register,
        control,
        reset,
        setValue,
        formState: { errors },
    } = methods;

    // Handle file selection
    const handleFileChange = useCallback((event) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            if (file.size > 10 * 1024 * 1024) {
                setFileError('File size too large. Max 10MB');
                return;
            }
            if (!['image/png', 'image/jpeg', 'image/gif'].includes(file.type)) {
                setFileError('Only image files are allowed (PNG, JPG, GIF)');
                return;
            }
            setFileError('');
            setSelectedFiles([file]);
            setValue('attachments', files); // Update react-hook-form
        } else {
            setFileError('Please select an image file');
            setSelectedFiles([]);
            setValue('attachments', null);
        }
    }, [setValue]);

    // Handle drag and drop
    const handleDrag = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const file = files[0];
            if (file.size > 10 * 1024 * 1024) {
                setFileError('File size too large. Max 10MB');
                return;
            }
            if (!['image/png', 'image/jpeg', 'image/gif'].includes(file.type)) {
                setFileError('Only image files are allowed (PNG, JPG, GIF)');
                return;
            }
            setFileError('');
            setSelectedFiles([file]);
            setValue('attachments', files); // Update react-hook-form
        } else {
            setFileError('Please drop an image file');
        }
    }, [setValue]);

const onSubmit = async (data) => {
    try {
        const formData = new FormData();
        formData.append('fullName', data.fullName);
        formData.append('mobile', data.mobile);
        formData.append('subject', data.subject);
        formData.append('description', data.description);

        if (selectedFiles.length > 0) {
            formData.append('attachments', selectedFiles[0]);
        }

        const resp = await dispatch(addHelpThunk(formData)).unwrap();

        if (resp?.status === 200) {
            toast.success(resp?.message || "Request submitted successfully!");
        } else {
            toast.error(resp?.message || "Something went wrong!");
        }

        reset();
        setSelectedFiles([]);
        setFileError('');
        
    } catch (error) {
        console.error("Submission error:", error);
        toast.error(error?.message || "Submission failed!");

        if (typeof error === 'string' && error.toLowerCase().includes('file')) {
            setFileError(true);
        } else {
            setFileError(false);
        }
    }
};


    const onRecaptchaChange = async (recaptchaToken) => {
        if (recaptchaToken) {
            try {
                const response = await axios.post("https://localhost:2000/submit", {
                    recaptchaToken,
                });
                console.log(response, "responseresponse");
            } catch (error) {
                console.error('Error verifying reCAPTCHA:', error);
                toast.error(error.response?.data?.message || 'reCAPTCHA verification failed. Please try again.');
            }
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-200 to-purple-200 rounded-full opacity-20 blur-3xl"></div>
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-indigo-200 to-blue-200 rounded-full opacity-20 blur-3xl"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-purple-100 to-blue-100 rounded-full opacity-10 blur-3xl"></div>
            </div>

            <div className="max-w-3xl mx-auto relative">
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8 lg:p-10 border border-white/20 hover:shadow-3xl transition-all duration-500 animate-fade-in-up">
                    <div className="flex items-center mb-8 pb-6 border-b border-gray-100">
                        <div className="w-12 h-12 bg-gradient-to-r from-[#50B5FF] to-[#3A9FE8] rounded-xl flex items-center justify-center mr-4  ">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                            </svg>
                        </div>
                        <div>
                            <h2 className="text-3xl font-bold text-gray-800 mb-2">
                                Create Support Ticket
                            </h2>
                            <p className="text-gray-600 leading-relaxed">
                                Before creating a ticket, please check our FAQ and Knowledge Base for quick solutions.
                            </p>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="group">
                                <label className="flex items-center text-sm font-semibold text-gray-700 mb-3">
                                    <svg className="w-5 h-5 mr-2 text-[#50B5FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                    Full Name
                                </label>
                                <FormInput
                                    name="fullName"
                                    type="text"
                                    className="w-full pl-5 pr-4 py-3 border-2 border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-4 focus:ring-[#D5EDFF] focus:border-[#50B5FF] transition-all duration-300 hover:border-gray-300 bg-gray-50/50 hover:bg-white"
                                    placeholder="Enter your full name"
                                    register={register}
                                    errors={errors}
                                    control={control}
                                />
                            </div>
                            <div className="group">
                                <label className="flex items-center text-sm font-semibold text-gray-700 mb-3">
                                    <svg className="w-5 h-5 mr-2 text-[#50B5FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                    </svg>
                                    Mobile Number
                                </label>
                                <FormInput
                                    name="mobile"
                                    type="text"
                                    className="w-full pl-5 pr-4 py-3 border-2 border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-4 focus:ring-[#D5EDFF] focus:border-[#50B5FF] transition-all duration-300 hover:border-gray-300 bg-gray-50/50 hover:bg-white"
                                    placeholder="Enter your mobile number"
                                    register={register}
                                    errors={errors}
                                    control={control}
                                />
                            </div>
                        </div>

                        <div className="group">
                            <label className="flex items-center text-sm font-semibold text-gray-700 mb-3">
                                <svg className="w-5 h-5 mr-2 text-[#50B5FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                                </svg>
                                Subject
                            </label>
                            <FormInput
                                name="subject"
                                type="text"
                                className="w-full pl-5 pr-4 py-3 border-2 border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-4 focus:ring-[#D5EDFF] focus:border-[#50B5FF] transition-all duration-300 hover:border-gray-300 bg-gray-50/50 hover:bg-white"
                                placeholder="What's this about? 🤔"
                                register={register}
                                errors={errors}
                                control={control}
                            />
                        </div>

                        <div className="group">
                            <label className="flex items-center text-sm font-semibold text-gray-700 mb-3">
                                <svg className="w-5 h-5 mr-2 text-[#50B5FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                </svg>
                                Upload File
                            </label>
                            <div
                                className={`relative border-2 border-dashed rounded-2xl p-4 transition-all duration-300 bg-gray-50/30 ${dragActive ? 'border-[#50B5FF] bg-[#D5EDFF]/30' : 'border-gray-300 hover:border-[#50B5FF] hover:bg-[#D5EDFF]/20'
                                    }`}
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                            >
                                <div className="text-center">
                                    <svg className={`mx-auto h-12 w-12 transition-colors ${dragActive ? 'text-[#50B5FF]' : 'text-gray-400'}`} stroke="currentColor" fill="none" viewBox="0 0 48 48">
                                        <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <div className="mt-4">
                                        {/* Hidden input, only triggered by label click */}
                                        <input
                                            id="file-upload"
                                            type="file"
                                            name="attachments"
                                            {...register('attachments')}
                                            accept="image/png,image/jpeg,image/gif"
                                            className="hidden"
                                            onChange={handleFileChange}
                                        />

                                        {/* Label that only wraps 'Click to upload' */}
                                        <p className="text-sm text-gray-600">
                                            <label
                                                htmlFor="file-upload"
                                                className="font-medium text-[#50B5FF] hover:text-[#3A9FE8] cursor-pointer"
                                            >
                                                Click to upload
                                            </label>{" "}
                                            or drag and drop
                                        </p>

                                        <p className="text-xs text-gray-500 mt-1">PNG, JPG, GIF up to 10MB</p>

                                        {selectedFiles.length > 0 && (
                                            <div className="mt-3 text-sm text-gray-700">
                                                <p>Selected file: {selectedFiles[0].name}</p>
                                                <p className="text-xs">
                                                    Size: {(selectedFiles[0].size / 1024 / 1024).toFixed(2)} MB
                                                </p>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setSelectedFiles([]);
                                                        setValue('attachments', null);
                                                        setFileError('');
                                                    }}
                                                    className="mt-2 text-xs text-red-500 hover:text-red-700"
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        )}

                                        {(fileError || errors.attachments?.message) && (
                                            <p className="mt-2 text-xs text-red-500">
                                                {fileError || errors.attachments.message}
                                            </p>
                                        )}
                                    </div>

                                </div>
                            </div>
                        </div>

                        <div className="group">
                            <label className="flex items-center text-sm font-semibold text-gray-700 mb-3">
                                <svg className="w-5 h-4 mr-2 text-[#50B5FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Description
                            </label>
                            <FormInput
                                name="description"
                                type="textarea"
                                className="w-full pl-5 pr-4 py-3 border-2 border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-4 focus:ring-[#D5EDFF] focus:border-[#50B5FF] transition-all duration-300 hover:border-gray-300 resize-vertical min-h-[140px] bg-gray-50/50 hover:bg-white"
                                placeholder="Please describe your issue in detail... We're here to help! 💬"
                                rows={5}
                                register={register}
                                errors={errors}
                                control={control}
                            />
                        </div>

                        <div className="pt-6">
                            <button
                                type="submit"
                                disabled={addHelpThunkLoading}
                                className="group relative w-full bg-gradient-to-r from-[#50B5FF] to-[#3A9FE8] text-white font-bold py-3 px-8 rounded-2xl transition-all duration-300 hover:from-[#3A9FE8] hover:to-[#2B8BD1] hover:shadow-2xl hover:shadow-blue-500/25 focus:outline-none focus:ring-4 focus:ring-[#D5EDFF] disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-3 text-lg overflow-hidden"
                            >
                                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                                {addHelpThunkLoading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                                        <span>Sending Your Message...</span>
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-6 h-6 transition-transform duration-300 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                        </svg>
                                        <span>Send Message 🚀</span>
                                        <svg className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                        </svg>
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>

                <div className="text-center mt-12 p-6 bg-white/60 backdrop-blur-sm rounded-2xl border border-white/20 animate-fade-in-up">
                    <div className="flex items-center justify-center mb-4">
                        <div className="flex -space-x-2">
                            <div className="w-10 h-10 bg-gradient-to-r from-green-400 to-green-500 rounded-full flex items-center justify-center border-2 border-white  ">
                                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="w-10 h-10 bg-gradient-to-r from-blue-400 to-blue-500 rounded-full flex items-center justify-center border-2 border-white  ">
                                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                </svg>
                            </div>
                            <div className="w-10 h-10 bg-gradient-to-r from-purple-400 to-purple-500 rounded-full flex items-center justify-center border-2 border-white  ">
                                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                            </div>
                        </div>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Need Immediate Help? 🆘</h3>
                    <p className="text-gray-600 mb-4">Our support team typically responds within 2-4 hours</p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm">
                        <a href="mailto:support@example.com" className="flex items-center text-[#50B5FF] hover:text-[#3A9FE8] font-medium group transition-colors">
                            <svg className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                            </svg>
                            chatinject@gmail.com
                        </a>
                        <span className="hidden sm:block text-gray-300">|</span>
                        <a href="tel:+1234567890" className="flex items-center text-[#50B5FF] hover:text-[#3A9FE8] font-medium group transition-colors">
                            <svg className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            +91 84699 42899
                        </a>
                    </div>
                </div>
            </div>

            <style jsx>{`
                @keyframes fade-in-down {
                    0% {
                        opacity: 0;
                        transform: translateY(-20px);
                    }
                    100% {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                @keyframes fade-in-up {
                    0% {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    100% {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                .animate-fade-in-down {
                    animation: fade-in-down 0.8s ease-out;
                }
                
                .animate-fade-in-up {
                    animation: fade-in-up 0.8s ease-out 0.2s both;
                }
            `}</style>
        </div>
    );
};

export default HelpAndSupport;